import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import { useLocaleStore } from '@/stores/locale.store'
import { routeTranslations } from './routes'
import { routeDefs } from './routeDefs'
import { DEFAULT_LOCALE, PREFIX_LOCALES, type Locale } from './locales'
import { normalizeLocale } from '@/utils/locale'

function isPrefixLocale(value: string): value is Exclude<Locale, typeof DEFAULT_LOCALE> {
  return (PREFIX_LOCALES as readonly string[]).includes(value)
}

function childrenFor(lang: Locale): RouteRecordRaw[] {
  return routeDefs.map((def) => {
    const path = routeTranslations[def.key][lang] ?? ''
    const name =
      def.nameBase
        ? (lang === DEFAULT_LOCALE ? def.nameBase : `${def.nameBase}-${lang}`)
        : undefined

    return {
      path,
      component: def.component,
      ...(def.meta ? { meta: def.meta } : {}),
      ...(name ? { name } : {}),
    }
  })
}

const routes: RouteRecordRaw[] = [
  // default locale at root
  {
    path: '/',
    children: childrenFor(DEFAULT_LOCALE)
  },

  // only the locales that should have a prefix
  ...PREFIX_LOCALES.map((lang) => ({
    path: `/${lang}`,
    children: childrenFor(lang)
  })),

  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => {
      const firstSeg = normalizeLocale(to.path.split('/')[1] ?? '')
      const lang: Locale = isPrefixLocale(firstSeg) ? firstSeg : DEFAULT_LOCALE
      const notFoundPath = routeTranslations.notFound[lang]

      return lang === DEFAULT_LOCALE ? `/${notFoundPath}` : `/${lang}/${notFoundPath}`
    }
  }
]


export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

router.afterEach((to) => {
  const store = useLocaleStore()

  const firstSeg = normalizeLocale(to.path.split('/')[1] ?? '')

  const lang: Locale = isPrefixLocale(firstSeg) ? firstSeg : DEFAULT_LOCALE

  if (store.currentLocale !== lang) {
    store.setLocale(lang)
  }

  localStorage.setItem('locale', lang)
})
